import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, CardContent, Typography, TextField, Button, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Grid, Box,
  Select, MenuItem, FormControl, InputLabel, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Tooltip,
  Pagination, CircularProgress
} from '@mui/material';
import { Delete, Edit, Save, Cancel, ArrowUpward, ArrowDownward, Add, Close, People } from '@mui/icons-material';
import { useParams } from 'react-router';
import { useQueryParams } from '../hooks/useQueryParams';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiHelpers';
import AccountPointsOfContact from './AccountPointsOfContact';

function Accounts() {
  const { id } = useParams();
  const { queryParams, setQueryParam } = useQueryParams();
  
  // Get user from token verification (will be passed from parent or context)
  const [user, setUser] = useState(null);
  const canEdit = user && ['admin', 'account_onboarding'].includes(user.role);
  
  // Verify token and get user info
  useEffect(() => {
    const checkUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await apiGet('/api/verify-token');
          setUser(response.data.user);
        } catch (error) {
          console.warn('Token verification failed:', error);
        }
      }
    };
    checkUser();
  }, []);
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 30,
    hasNext: false,
    hasPrevious: false
  });
  const [searchInfo, setSearchInfo] = useState({
    searchTerm: '',
    totalMatches: 0,
    firstMatchPage: null
  });
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem('accountsPageSize')) || 30);
  const [currentPage, setCurrentPage] = useState(parseInt(queryParams.page) || 1);
  const [searchTerm, setSearchTerm] = useState(queryParams.search || '');
  const [sortField, setSortField] = useState(queryParams.sort || 'account_name');
  const [sortOrder, setSortOrder] = useState(queryParams.order || 'asc');
  const [newAccount, setNewAccount] = useState({ 
    account_name: '', 
    status: 'active', 
    state: '', 
    managed_by: '',
    medtronic_status: 'active',
    boston_scientific_status: 'active',
    abbott_status: 'active',
    biotronik_status: 'active',
    remarks: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState({ 
    account_name: '', 
    status: 'active', 
    state: '', 
    managed_by: 'Evoke',
    medtronic_status: 'inactive',
    boston_scientific_status: 'inactive',
    abbott_status: 'inactive',
    biotronik_status: 'inactive',
    remarks: ''
  });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [contactsCounts, setContactsCounts] = useState({});
  const [statusCounts, setStatusCounts] = useState({ total: 0, active: 0, inactive: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Debounced fetch to prevent excessive API calls
    const timeoutId = setTimeout(() => {
      fetchAccounts();
    }, searchTerm ? 300 : 0); // Debounce search, immediate for other changes
    
    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    // Load status counts on initial load
    loadStatusCounts();
  }, []);

  useEffect(() => {
    // Update URL params
    setQueryParam('page', currentPage > 1 ? currentPage.toString() : '');
    setQueryParam('search', searchTerm);
    setQueryParam('sort', sortField !== 'account_name' ? sortField : '');
    setQueryParam('order', sortOrder !== 'asc' ? sortOrder : '');
  }, [currentPage, searchTerm, sortField, sortOrder]);

  // Handle direct account access via URL parameter
  useEffect(() => {
    if (id && accounts.length > 0) {
      const account = accounts.find(acc => acc.account_id === parseInt(id));
      if (account && canEdit) {
        startEdit(account);
      }
    }
  }, [id, accounts, canEdit]);

  const fetchAccounts = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Input validation
      const validPage = Math.max(1, currentPage);
      const validPageSize = Math.min(500, Math.max(10, pageSize));
      const validSearchTerm = searchTerm.trim().substring(0, 100);
      
      const params = new URLSearchParams({
        page: validPage.toString(),
        limit: validPageSize.toString(),
        sort: sortField,
        order: sortOrder
      });
      
      if (validSearchTerm) {
        params.append('search', validSearchTerm);
      }
      
      const response = await apiGet(`/api/accounts?${params.toString()}`);
      
      // Validate response structure
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setAccounts(response.data.data);
        setPagination(response.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: validPageSize,
          hasNext: false,
          hasPrevious: false
        });
        setSearchInfo(response.data.searchInfo || {
          searchTerm: '',
          totalMatches: 0,
          firstMatchPage: null
        });
        
        // Load contact counts for current page accounts
        if (response.data.data.length > 0) {
          const accountIds = response.data.data.map(acc => acc.account_id).join(',');
          loadContactsCounts(accountIds);
        } else {
          setContactsCounts({});
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setError('Failed to load accounts. Please try again.');
      setAccounts([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: pageSize,
        hasNext: false,
        hasPrevious: false
      });
      setSearchInfo({
        searchTerm: '',
        totalMatches: 0,
        firstMatchPage: null
      });
    } finally {
      setLoading(false);
    }
  };

  const loadContactsCounts = async (accountIds = null) => {
    try {
      const url = accountIds ? `/api/accounts/contact-counts?ids=${accountIds}` : '/api/accounts/contact-counts';
      const response = await apiGet(url);
      setContactsCounts(response.data || {});
    } catch (error) {
      console.error('Error fetching points of contact counts:', error);
      // Set empty object to prevent UI errors
      setContactsCounts({});
    }
  };

  const loadStatusCounts = async () => {
    try {
      const response = await apiGet('/api/accounts/status-counts');
      
      // Validate response structure
      if (response.data && typeof response.data.total === 'number') {
        setStatusCounts({
          total: Math.max(0, response.data.total),
          active: Math.max(0, response.data.active || 0),
          inactive: Math.max(0, response.data.inactive || 0)
        });
      } else {
        setStatusCounts({ total: 0, active: 0, inactive: 0 });
      }
    } catch (error) {
      console.error('Error fetching status counts:', error);
      setStatusCounts({ total: 0, active: 0, inactive: 0 });
    }
  };

  const openContactsDialog = (account) => {
    setSelectedAccount(account);
    setContactsDialogOpen(true);
  };

  const closeContactsDialog = (shouldRefresh = false) => {
    setContactsDialogOpen(false);
    setSelectedAccount(null);
    // Reload counts immediately if needed
    if (shouldRefresh && accounts.length > 0) {
      const accountIds = accounts.map(acc => acc.account_id).join(',');
      loadContactsCounts(accountIds);
    }
  };

  const handleContactChange = () => {
    // Immediately refresh contact counts
    if (accounts.length > 0) {
      const accountIds = accounts.map(acc => acc.account_id).join(',');
      loadContactsCounts(accountIds);
    }
  };

  const handlePageChange = (event, newPage) => {
    if (newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = Math.min(500, Math.max(10, parseInt(event.target.value) || 30));
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
    try {
      localStorage.setItem('accountsPageSize', newSize.toString());
    } catch (e) {
      console.warn('Failed to save page size preference:', e);
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value.substring(0, 100); // Limit search length
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpward sx={{ fontSize: 18, color: 'grey.400' }} />;
    }
    return sortOrder === 'asc' ? 
      <ArrowUpward sx={{ fontSize: 18, color: 'primary.main' }} /> :
      <ArrowDownward sx={{ fontSize: 18, color: 'primary.main' }} />;
  };

  const addAccount = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await apiPost('/api/accounts', newAccount);
      setNewAccount({ 
        account_name: '', 
        status: 'active', 
        state: '', 
        managed_by: '',
        medtronic_status: 'active',
        boston_scientific_status: 'active',
        abbott_status: 'active',
        biotronik_status: 'active',
        remarks: ''
      });
      setAddDialogOpen(false);
      setSuccess('Account created successfully');
      setTimeout(() => setSuccess(''), 3000);
      // Go to first page to see new account and refresh status counts
      setCurrentPage(1);
      fetchAccounts();
      loadStatusCounts();
    } catch (error) {
      console.error('Failed to add account:', error);
      setError(error.response?.data?.error || 'Failed to create account');
    }
  };

  const updateAccount = async (id, updatedAccount) => {
    try {
      await apiPut(`/api/accounts/${id}`, updatedAccount);
      setEditingId(null);
      fetchAccounts();
    } catch (error) {
      console.error('Failed to update account:', error);
    }
  };

  const deleteAccount = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await apiDelete(`/api/accounts/${id}`);
        fetchAccounts();
      } catch (error) {
        console.error('Failed to delete account:', error);
      }
    }
  };

  const startEdit = (account) => {
    setEditingId(account.account_id);
    setEditValue({ 
      account_name: account.account_name,
      status: account.status || 'active',
      state: account.state || '',
      managed_by: account.managed_by || '',
      medtronic_status: account.medtronic_status || 'active',
      boston_scientific_status: account.boston_scientific_status || 'active',
      abbott_status: account.abbott_status || 'active',
      biotronik_status: account.biotronik_status || 'active',
      remarks: account.remarks || ''
    });
    setEditDialogOpen(true);
  };

  const handleAccountNameClick = (account) => {
    if (canEdit) {
      startEdit(account);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await apiPut(`/api/accounts/${editingId}`, editValue);
      setEditDialogOpen(false);
      setEditingId(null);
      setSuccess('Account updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      // Force immediate refresh without cache
      await fetchAccounts();
      await loadStatusCounts();
    } catch (error) {
      console.error('Failed to update account:', error);
      setError(error.response?.data?.error || 'Failed to update account');
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingId(null);
    setError('');
  };



  return (
    <Box sx={{ pt: 0.5 }}>
      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  Evoke Medical Care Accounts List
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Total: {statusCounts.total}
                    </Typography>
                    <Tooltip title="Accounts where at least one manufacturer (Medtronic, Boston Scientific, Abbott, or Biotronik) has 'active' status">
                      <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 500, cursor: 'help' }}>
                        Active: {statusCounts.active}
                      </Typography>
                    </Tooltip>
                    <Tooltip title="Accounts where all manufacturers have non-active status (inactive or n/a)">
                      <Typography variant="body1" sx={{ color: 'warning.main', fontWeight: 500, cursor: 'help' }}>
                        Inactive: {statusCounts.inactive}
                      </Typography>
                    </Tooltip>
                    {searchInfo?.searchTerm && (
                      <Typography variant="body1" sx={{ color: 'info.main', fontWeight: 500 }}>
                        Found: {searchInfo.totalMatches} matches
                      </Typography>
                    )}
                  </Box>
                  {canEdit && (
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => setAddDialogOpen(true)}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3
                      }}
                    >
                      Add Account
                    </Button>
                  )}
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                    lg: 4
                  }}>
                  <TextField
                    fullWidth
                    label="Search Accounts"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Grid>
              </Grid>

              <TableContainer component={Paper} sx={{ 
                maxHeight: 600, 
                overflow: 'auto',
                minWidth: 800,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: 'grey.50', fontWeight: 600 } }}>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', minWidth: 200 }} onClick={() => handleSort('account_name')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Account
                          {getSortIcon('account_name')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', minWidth: 80 }} onClick={() => handleSort('state')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          State
                          {getSortIcon('state')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', minWidth: 120 }} onClick={() => handleSort('managed_by')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Managed By
                          {getSortIcon('managed_by')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }} onClick={() => handleSort('medtronic_status')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Medtronic
                          {getSortIcon('medtronic_status')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', minWidth: 120 }} onClick={() => handleSort('boston_scientific_status')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Boston Sci.
                          {getSortIcon('boston_scientific_status')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }} onClick={() => handleSort('abbott_status')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Abbott
                          {getSortIcon('abbott_status')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', minWidth: 100 }} onClick={() => handleSort('biotronik_status')}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Biotronik
                          {getSortIcon('biotronik_status')}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Points of Contact</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} sx={{ mr: 2 }} />
                          Loading accounts...
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Alert severity="error" sx={{ maxWidth: 400, mx: 'auto' }}>
                            {error}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    ) : accounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          {searchTerm ? `No accounts found matching "${searchTerm.substring(0, 50)}${searchTerm.length > 50 ? '...' : ''}"` : 'No accounts found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      accounts.map((account) => (
                      <TableRow key={account.account_id} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                        <TableCell>
                          <Typography 
                            sx={{ 
                              cursor: canEdit ? 'pointer' : 'default',
                              '&:hover': canEdit ? { textDecoration: 'underline' } : {}
                            }}
                            onClick={() => handleAccountNameClick(account)}
                          >
                            {account.account_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{account.state || ''}</TableCell>
                        <TableCell>{account.managed_by || ''}</TableCell>
                        <TableCell>
                          <Chip 
                            label={account.medtronic_status === 'active' ? 'Active' : account.medtronic_status === 'n/a' ? 'N/A' : 'Inactive'}
                            color={account.medtronic_status === 'active' ? 'success' : account.medtronic_status === 'n/a' ? 'default' : 'warning'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, minWidth: 60 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={account.boston_scientific_status === 'active' ? 'Active' : account.boston_scientific_status === 'n/a' ? 'N/A' : 'Inactive'}
                            color={account.boston_scientific_status === 'active' ? 'success' : account.boston_scientific_status === 'n/a' ? 'default' : 'warning'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, minWidth: 60 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={account.abbott_status === 'active' ? 'Active' : account.abbott_status === 'n/a' ? 'N/A' : 'Inactive'}
                            color={account.abbott_status === 'active' ? 'success' : account.abbott_status === 'n/a' ? 'default' : 'warning'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, minWidth: 60 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={account.biotronik_status === 'active' ? 'Active' : account.biotronik_status === 'n/a' ? 'N/A' : 'Inactive'}
                            color={account.biotronik_status === 'active' ? 'success' : account.biotronik_status === 'n/a' ? 'default' : 'warning'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, minWidth: 60 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={`${contactsCounts[account.account_id] || 0} contacts`}
                              size="small"
                              color={contactsCounts[account.account_id] > 0 ? 'primary' : 'default'}
                            />
                            <Button
                              size="small"
                              startIcon={<People />}
                              onClick={() => openContactsDialog(account)}
                              sx={{ 
                                textTransform: 'none',
                                minWidth: 'auto',
                                px: 1
                              }}
                            >
                              {canEdit ? 'Manage' : 'View'}
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {account.remarks || ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination Controls */}
              {!loading && (pagination?.totalCount || 0) > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, pagination?.totalCount || 0)} of {pagination?.totalCount || 0} entries
                      {searchInfo?.searchTerm && ` (filtered)`}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        Show:
                      </Typography>
                      <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          minWidth: 80,
                          height: 32,
                          '& .MuiSelect-select': {
                            py: 0.5,
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value={30}>30</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                  
                  {(pagination?.totalPages || 0) > 1 && (
                    <Pagination
                      count={pagination?.totalPages || 1}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      size="medium"
                    />
                  )}
                </Box>
              )}
        </CardContent>
      </Card>

      {/* Success Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Add Account Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3 }
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add New Account
          </Typography>
          <IconButton
            onClick={() => setAddDialogOpen(false)}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={addAccount}>
          <DialogContent sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={newAccount.account_name}
                  onChange={(e) => setNewAccount({...newAccount, account_name: e.target.value})}
                  required
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="State (2 chars)"
                  value={newAccount.state}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().slice(0, 2);
                    setNewAccount({...newAccount, state: value});
                  }}
                  placeholder="CA, NY, TX..."
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  slotProps={{
                    htmlInput: { maxLength: 2 }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="Managed By"
                  value={newAccount.managed_by}
                  onChange={(e) => setNewAccount({...newAccount, managed_by: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Medtronic"
                  value={newAccount.medtronic_status}
                  onChange={(e) => setNewAccount({...newAccount, medtronic_status: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="n/a">N/A</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Boston Scientific"
                  value={newAccount.boston_scientific_status}
                  onChange={(e) => setNewAccount({...newAccount, boston_scientific_status: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="n/a">N/A</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Abbott"
                  value={newAccount.abbott_status}
                  onChange={(e) => setNewAccount({...newAccount, abbott_status: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="n/a">N/A</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Biotronik"
                  value={newAccount.biotronik_status}
                  onChange={(e) => setNewAccount({...newAccount, biotronik_status: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="n/a">N/A</MenuItem>
                </TextField>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={newAccount.remarks}
                  onChange={(e) => setNewAccount({...newAccount, remarks: e.target.value})}
                  multiline
                  rows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => setAddDialogOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3
              }}
            >
              Add Account
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleEditCancel}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3 }
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Account
          </Typography>
          <IconButton
            onClick={handleEditCancel}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={editValue.account_name}
                  onChange={(e) => setEditValue({...editValue, account_name: e.target.value})}
                  required
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="State (2 chars)"
                  value={editValue.state}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().slice(0, 2);
                    setEditValue({...editValue, state: value});
                  }}
                  placeholder="CA, NY, TX..."
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  slotProps={{
                    htmlInput: { maxLength: 2 }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="Managed By"
                  value={editValue.managed_by}
                  onChange={(e) => setEditValue({...editValue, managed_by: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Medtronic"
                  value={editValue.medtronic_status}
                  onChange={(e) => setEditValue({...editValue, medtronic_status: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="n/a">N/A</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Boston Scientific"
                  value={editValue.boston_scientific_status}
                  onChange={(e) => setEditValue({...editValue, boston_scientific_status: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="n/a">N/A</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Abbott"
                  value={editValue.abbott_status}
                  onChange={(e) => setEditValue({...editValue, abbott_status: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="n/a">N/A</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Biotronik"
                  value={editValue.biotronik_status}
                  onChange={(e) => setEditValue({...editValue, biotronik_status: e.target.value})}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="n/a">N/A</MenuItem>
                </TextField>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={editValue.remarks}
                  onChange={(e) => setEditValue({...editValue, remarks: e.target.value})}
                  multiline
                  rows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, justifyContent: 'space-between' }}>
            <Button 
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this account?')) {
                  deleteAccount(editingId);
                  setEditDialogOpen(false);
                  setEditingId(null);
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              Delete Account
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                onClick={handleEditCancel}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained"
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3
                }}
              >
                Save Changes
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      {/* Points of Contact Dialog */}
      <AccountPointsOfContact
        open={contactsDialogOpen}
        onClose={closeContactsDialog}
        account={selectedAccount}
        onContactChange={handleContactChange}
      />
    </Box>
  );
}

export default Accounts;